import React, { useMemo, useCallback } from 'react';
import moment from 'moment';
import intl from 'react-intl-universal';

import { If } from '@/components/Utils/If';
import { FormattedMessage as T } from '@/components/FormattedMessage';
import {
  FinancialSheetRoot,
  FinancialSheetFooterCurrentTime,
  FinancialSheetFooterBasis,
  FinancialSheetFooter,
  FinancialSheetAccountingBasis,
  FinancialSheetTable,
  FinancialSheetDate,
  FinancialSheetType,
  FinancialSheetTitle,
} from './StyledFinancialSheet';

/**
 * Financial sheet.
 * @returns {React.JSX}
 */
interface FinancialSheetProps {
  companyName?: React.ReactNode;
  sheetType?: React.ReactNode;
  fromDate?: moment.MomentInput;
  toDate?: moment.MomentInput;
  asDate?: moment.MomentInput;
  children?: React.ReactNode;
  accountingBasis?: React.ReactNode;
  basis?: 'cash' | 'accrual' | string;
  minimal?: boolean;
  fullWidth?: boolean;
  currentDate?: boolean;
  className?: string;
  loading?: boolean;
  name?: string;
}

export function FinancialSheet({
  companyName,
  sheetType,
  fromDate,
  toDate,
  asDate,
  children,
  accountingBasis,
  basis,
  minimal = false,
  fullWidth = false,
  currentDate = true,
  className,
}: FinancialSheetProps) {
  const format = 'DD/MM/YYYY';
  const formattedFromDate = useMemo(
    () => moment(fromDate).format(format),
    [fromDate],
  );
  const formattedToDate = useMemo(
    () => moment(toDate).format(format),
    [toDate],
  );
  const formattedAsDate = useMemo(
    () => moment(asDate).format(format),
    [asDate],
  );
  const methodsLabels = useMemo(
    () => ({
      cash: intl.get('cash'),
      accrual: intl.get('accrual'),
    }),
    [],
  );
  const getBasisLabel = useCallback(
    (b: string) => methodsLabels[b as keyof typeof methodsLabels],
    [methodsLabels],
  );
  const basisLabel = useMemo(
    () => (basis ? getBasisLabel(basis) : undefined),
    [getBasisLabel, basis],
  );

  return (
    <FinancialSheetRoot
      minimal={minimal}
      fullWidth={fullWidth}
      className={className}
    >
      {companyName && <FinancialSheetTitle>{companyName}</FinancialSheetTitle>}
      {sheetType && <FinancialSheetType>{sheetType}</FinancialSheetType>}

      <FinancialSheetDate>
        <If condition={asDate}>
          <T id={'as'} /> {formattedAsDate}
        </If>
        <If condition={fromDate && toDate}>
          <T id={'from'} /> {formattedFromDate} | <T id={'to'} />{' '}
          {formattedToDate}
        </If>
      </FinancialSheetDate>

      <FinancialSheetTable>{children}</FinancialSheetTable>
      <FinancialSheetAccountingBasis>
        {accountingBasis}
      </FinancialSheetAccountingBasis>

      <FinancialSheetFooter>
        {basisLabel && (
          <FinancialSheetFooterBasis>
            <T id={'accounting_basis'} /> {basisLabel}
          </FinancialSheetFooterBasis>
        )}
        {currentDate && (
          <FinancialSheetFooterCurrentTime>
            {moment().format('DD/MM/YYYY HH:mm')}
          </FinancialSheetFooterCurrentTime>
        )}
      </FinancialSheetFooter>
    </FinancialSheetRoot>
  );
}
